import classNames from 'classnames';
import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import {
  Choice,
  Language,
  PatientGender,
  ReferralMappingOperationType,
  ReferralMappingRosterType,
} from '@vestahealthcare/common/enums';
import Enum, {
  Selectable,
  SelectableInfo,
} from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Organization,
  ReferralIngestionMapperColumn,
  ReferralIngestionMapperSourceColumn,
  ReferralMappingOperation,
} from '@vestahealthcare/common/models';
import { ReferralMappingMappedValue } from '@vestahealthcare/common/models/ReferralMappingMappedValue';

import { Arrow, IconButton, Select, TextInput, Tooltip } from 'styleguide-v2';

import { excludeOtherValues } from 'dash/src/utils/dataUtils';
import { capitalize } from 'lodash';

import { generateCandidateDestinationColumns } from '../../Ops/MemberIngestion/MemberTableColumns';
import { Rule, useStyles } from './ReferralIngestionMapperEditModal';

const SPLIT_OPTIONS = [
  { value: '1', label: translate('referrals.ingestionMapper.modal.first') },
  { value: '2', label: translate('referrals.ingestionMapper.modal.second') },
  { value: '-1', label: translate('referrals.ingestionMapper.modal.last') },
];

const NO_MATCH_OPTIONS = [
  {
    value: 'ACCEPT',
    label: translate('referrals.ingestionMapper.modal.keepOriginalValue'),
  },
  {
    value: 'DISCARD',
    label: translate('referrals.ingestionMapper.modal.discardOriginalValue'),
  },
];

interface MappingState {
  rules: Rule[];
  setRules: React.Dispatch<React.SetStateAction<Rule[]>>;
}
interface FileData {
  headers: ReferralIngestionMapperSourceColumn[];
  branches: string[];
  genders: string[];
  languages: string[];
  booleanColumns: Record<string, string[]>;
  relationshipColumns: Record<string, string[]>;
}

interface LoadingState {
  data: boolean;
  fileBranches: boolean;
  fileGenders: boolean;
  fileLanguages: boolean;
  booleanColumns: Record<string, boolean>;
  relationshipColumns: Record<string, boolean>;
}

interface SpecialColumns {
  branch?: Rule;
  gender?: Rule;
  language?: Rule;
}

interface MappingRuleRowProps {
  item: Rule;
  idx: number;
  rosterType: string;
  submitted: boolean;
  candidateTotal: number;
  memberMapping: MappingState;
  candidateMapping: MappingState;
  fileData: FileData;
  file?: Selectable;
  referral: SelectableInfo<Organization>[];
  mixedReferrals: SelectableInfo<Organization>[];
  mixedFile?: boolean;
  loading: LoadingState;
  destinationColumns: SelectableInfo<ReferralIngestionMapperColumn>[];
  relationships: SelectableInfo<any>[];
  specialColumns: SpecialColumns;
  updateBooleanFileValues: (
    sourceColumn: string,
    sourceColumnIndex: number,
    modelColumn: Rule,
    file?: Selectable,
  ) => Promise<void>;
  updateRelationshipFileValues: (
    sourceColumn: string,
    sourceColumnIndex: number,
    modelColumn: Rule,
    file?: Selectable,
  ) => Promise<void>;
  renderMappingOptions: (
    operation: ReferralMappingOperation,
    sourceItems: string[],
    destinationItemLabel: string,
    destinationItems: SelectableInfo<any>[],
    destinationItemsGrouped?: any,
    keySuffix?: string,
    rosterType?: string,
  ) => React.ReactNode;
}

export const MappingRuleRow = ({
  item,
  idx,
  rosterType,
  submitted,
  candidateTotal,
  memberMapping,
  candidateMapping,
  fileData,
  file,
  referral,
  mixedReferrals,
  mixedFile,
  loading,
  destinationColumns,
  relationships,
  specialColumns,
  updateBooleanFileValues,
  updateRelationshipFileValues,
  renderMappingOptions,
}: MappingRuleRowProps) => {
  const isCandidate = rosterType === ReferralMappingRosterType.CANDIDATE.value;
  const currentMapping = isCandidate ? candidateMapping : memberMapping;
  const currentRules = currentMapping.rules;
  const setCurrentRules = currentMapping.setRules;
  const styles = useStyles();

  const getAvailableOperations = () => {
    if (isCandidate) {
      return [
        { value: -1, label: translate('global.none') },
        ...Enum.toSelectable(
          ReferralMappingOperationType.asArray.filter(
            (operationType) =>
              operationType !== ReferralMappingOperationType.DEFAULT &&
              operationType !== ReferralMappingOperationType.BRANCH &&
              operationType !== ReferralMappingOperationType.SPLIT &&
              operationType !== ReferralMappingOperationType.DATE &&
              operationType !== ReferralMappingOperationType.GENDER &&
              operationType !== ReferralMappingOperationType.LANGUAGE,
          ),
        ),
      ];
    }

    return [
      { value: -1, label: translate('global.none') },
      ...Enum.toSelectable(
        ReferralMappingOperationType.asArray.filter(
          (item) =>
            item !== ReferralMappingOperationType.DEFAULT &&
            ((!specialColumns.branch && mixedFile) ||
              item !== ReferralMappingOperationType.BRANCH) &&
            (!specialColumns.gender ||
              item !== ReferralMappingOperationType.GENDER) &&
            (!specialColumns.language ||
              item !== ReferralMappingOperationType.LANGUAGE) &&
            item !== ReferralMappingOperationType.SPLIT &&
            item !== ReferralMappingOperationType.BOOLEAN &&
            item !== ReferralMappingOperationType.RELATIONSHIP,
        ),
      ),
    ];
  };

  const getDestinationItems = () => {
    if (isCandidate) {
      return generateCandidateDestinationColumns(candidateTotal);
    }

    return excludeOtherValues<{ value: string }>(
      [...destinationColumns].sort(({ value: a }, { value: b }) =>
        a.toString().localeCompare(b.toString()),
      ) as { value: string }[],
      memberMapping.rules?.map(({ targetColumn }) => ({
        value: targetColumn || '',
      })),
      { value: item.targetColumn || '' },
    );
  };

  const getSourceFileHeaders = () => {
    if (isCandidate) {
      const usedColumns =
        memberMapping.rules
          ?.filter(({ sourceColumn }) => sourceColumn)
          ?.map(({ sourceColumn, sourceColumnIndex }) => ({
            sourceColumn,
            sourceColumnIndex,
          })) || [];
      return fileData.headers.filter(
        ({ name, index }) =>
          !usedColumns.find(
            (header) =>
              name === header.sourceColumn &&
              index === header.sourceColumnIndex,
          ),
      );
    }
    return fileData.headers;
  };

  const handleSourceColumnChange = (
    sourceColumn: ReferralIngestionMapperSourceColumn,
  ) => {
    const defaultValueText = translate(
      'referrals.ingestionMapper.modal.defaultValue',
    );

    if (sourceColumn.name === defaultValueText) {
      currentRules[idx].default = true;
      currentRules[idx].sourceColumn = '';
      delete currentRules[idx].operation;
    } else {
      currentRules[idx].default = false;
      currentRules[idx].defaultValue = '';
      currentRules[idx].sourceColumn = sourceColumn.name;
      currentRules[idx].sourceColumnIndex = sourceColumn.index;

      if (isCandidate) {
        if (
          currentRules[idx].operation?.action ===
          ReferralMappingOperationType.BOOLEAN
        ) {
          updateBooleanFileValues(
            sourceColumn.name,
            sourceColumn.index,
            currentRules[idx],
            file,
          );
        }

        if (
          currentRules[idx].operation?.action ===
          ReferralMappingOperationType.RELATIONSHIP
        ) {
          updateRelationshipFileValues(
            sourceColumn.name,
            sourceColumn.index,
            currentRules[idx],
            file,
          );
        }
      }
    }
    setCurrentRules([...currentRules]);
  };

  const handleDefaultValueChange = (defaultValue?: string) => {
    if (currentRules[idx].default && !item.readonly) {
      currentRules[idx].defaultValue = defaultValue;
      setCurrentRules([...currentRules]);
    }
  };

  const handleOperationChange = (type?: Selectable) => {
    if (currentRules[idx] && type && !item.readonly) {
      if (type.value === -1) {
        delete currentRules[idx].operation;
      } else {
        if (!currentRules[idx].operation) {
          currentRules[idx].operation = new ReferralMappingOperation({});
        }
        currentRules[idx].operation!.action =
          ReferralMappingOperationType.byKey[type.value];
        currentRules[idx].operation!.param1 = '';
        currentRules[idx].operation!.param2 = '';

        if (type.value === ReferralMappingOperationType.SPLIT_MULTIPLE.value) {
          currentRules[idx].operation!.dataDictionary = [
            new ReferralMappingMappedValue({}),
          ];
        }

        if (isCandidate) {
          if (
            type.value === ReferralMappingOperationType.BOOLEAN.value &&
            currentRules[idx].sourceColumn
          ) {
            updateBooleanFileValues(
              currentRules[idx].sourceColumn!,
              currentRules[idx].sourceColumnIndex!,
              currentRules[idx],
              file,
            );
          }

          if (
            type.value === ReferralMappingOperationType.RELATIONSHIP.value &&
            currentRules[idx].sourceColumn
          ) {
            updateRelationshipFileValues(
              currentRules[idx].sourceColumn!,
              currentRules[idx].sourceColumnIndex!,
              currentRules[idx],
              file,
            );
          }
        }
      }
      setCurrentRules([...currentRules]);
    }
  };

  const handleDestinationChange = (destination: Selectable) => {
    if (!item.readonly) {
      currentRules[idx].targetColumn = destination?.value as string;

      if (isCandidate) {
        const destinationColumn = generateCandidateDestinationColumns(
          candidateTotal,
        )?.find(({ value }) => value === destination?.value);
        currentRules[idx].candidateIndex =
          destinationColumn?.info?.candidateIndex || 0;
        currentRules[idx]!.rosterType =
          ReferralMappingRosterType.CANDIDATE.value;
      }

      setCurrentRules([...currentRules]);
    }
  };

  const handleAddTransformation = () => {
    currentRules[idx].operation = new ReferralMappingOperation({});
    setCurrentRules([...currentRules]);
  };

  const handleDeleteRule = () => {
    if (!item.readonly && currentRules.length >= 2) {
      currentRules.splice(idx, 1);
      setCurrentRules([...currentRules]);
    }
  };

  const sourceFileHeaders = getSourceFileHeaders();

  return (
    <>
      <div
        className={classNames(
          'flex middle',
          styles.row,
          item.readonly && 'bg-gray-50',
        )}
      >
        {item.readonly && (
          <Tooltip
            text={translate('referrals.ingestionMapper.modal.readonlyTooltip')}
          >
            <InfoIcon
              className={idx === 0 ? styles.firtsItem : ''}
              color="info"
              fontSize="small"
              style={{ marginRight: '0.5rem' }}
            />
          </Tooltip>
        )}

        <Select
          className={styles.select}
          disabled={
            !!item.readonly || !sourceFileHeaders?.length || !referral?.length
          }
          disableClearable
          error={submitted && !item.sourceColumn && !item.default}
          items={[
            {
              name: translate('referrals.ingestionMapper.modal.defaultValue'),
              index: 0,
            } as ReferralIngestionMapperSourceColumn,
            ...sourceFileHeaders,
          ]}
          getItemLabel={ReferralIngestionMapperSourceColumn.toString}
          grouped={(item: ReferralIngestionMapperSourceColumn) =>
            item.name ===
            translate('referrals.ingestionMapper.modal.defaultValue')
              ? ''
              : translate('common.column')
          }
          label={
            idx === 0
              ? translate('referrals.ingestionMapper.modal.source')
              : undefined
          }
          placeholder={translate('referrals.ingestionMapper.modal.source')}
          onChange={handleSourceColumnChange}
          renderOption={(item: ReferralIngestionMapperSourceColumn) =>
            item.name ===
            translate('referrals.ingestionMapper.modal.defaultValue') ? (
              <Tooltip
                text={
                  <>
                    <p className="white">
                      {translate(
                        'referrals.ingestionMapper.modal.defaultValueTooltip1',
                      )}
                    </p>
                    <p className="white no-margin">
                      {translate(
                        'referrals.ingestionMapper.modal.defaultValueTooltip2',
                      )}
                    </p>
                    <p className="white">
                      {translate(
                        'referrals.ingestionMapper.modal.defaultValueTooltip3',
                      )}
                    </p>
                    <p className="white no-margin">
                      {translate(
                        'referrals.ingestionMapper.modal.defaultValueTooltip4',
                      )}
                    </p>
                  </>
                }
                followCursor
                enterDelay={300}
              >
                <span className="flex min-gap">
                  {ReferralIngestionMapperSourceColumn.toString(item)}
                  <InfoIcon color="info" />
                </span>
              </Tooltip>
            ) : (
              ReferralIngestionMapperSourceColumn.toString(item)
            )
          }
          value={
            item?.default
              ? {
                  name: translate(
                    'referrals.ingestionMapper.modal.defaultValue',
                  ),
                  index: 0,
                }
              : { name: item.sourceColumn, index: item.sourceColumnIndex }
          }
          size="small"
        />

        {item.default ? (
          <>
            :
            <TextInput
              className={classNames(
                styles.select,
                idx === 0 && styles.firtsItem,
              )}
              disabled={!!item.readonly}
              error={submitted && !item.defaultValue}
              onChange={handleDefaultValueChange}
              placeholder={translate(
                'referrals.ingestionMapper.modal.defaultValue',
              )}
              value={item.defaultValue}
              size="small"
            />
            <Arrow className={classNames(idx === 0 && styles.firtsItem)} />
          </>
        ) : (
          <>
            {item?.operation ? (
              <>
                <Arrow className={classNames(idx === 0 && styles.firtsItem)} />
                {!item.readonly && (
                  <Select
                    className={classNames(
                      styles.selectExtraMin,
                      idx === 0 && styles.firtsItem,
                    )}
                    disableClearable
                    error={submitted && !item.operation}
                    getItemDisabled={
                      !isCandidate
                        ? (operation: Selectable) =>
                            !item.sourceColumn &&
                            [
                              ReferralMappingOperationType.BRANCH.value,
                              ReferralMappingOperationType.GENDER.value,
                              ReferralMappingOperationType.LANGUAGE.value,
                            ].includes(operation.value as string)
                        : undefined
                    }
                    items={getAvailableOperations()}
                    loading={
                      (!isCandidate &&
                        item.operation?.action &&
                        ((ReferralMappingOperationType.BRANCH ===
                          item.operation?.action &&
                          loading.fileBranches) ||
                          (ReferralMappingOperationType.GENDER ===
                            item.operation?.action &&
                            loading.fileGenders) ||
                          (ReferralMappingOperationType.LANGUAGE ===
                            item.operation?.action &&
                            loading.fileLanguages))) ||
                      (isCandidate &&
                        ((item.operation?.action ===
                          ReferralMappingOperationType.BOOLEAN &&
                          loading.booleanColumns[item.sourceColumn || '']) ||
                          (item.operation?.action ===
                            ReferralMappingOperationType.RELATIONSHIP &&
                            loading.relationshipColumns[
                              item.sourceColumn || ''
                            ])))
                    }
                    placeholder={translate(
                      'referrals.ingestionMapper.modal.transformation',
                    )}
                    onChange={handleOperationChange}
                    value={
                      item.operation?.action
                        ? Enum.toSelectable([item.operation.action])[0]
                        : undefined
                    }
                    size="small"
                  />
                )}

                {item.operation?.action ===
                  ReferralMappingOperationType.DATE && (
                  <>
                    <span
                      className={classNames(
                        idx === 0 && styles.firtsItem,
                        'semi-bold',
                      )}
                    >
                      {translate('referrals.ingestionMapper.modal.format')}:
                    </span>
                    <Select
                      allowAddNewValue
                      className={classNames(
                        styles.selectMin,
                        idx === 0 && styles.firtsItem,
                      )}
                      disableClearable
                      error={submitted && !item.operation?.param1}
                      items={[
                        { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd' },
                        { value: 'yy-MM-dd', label: 'yy-MM-dd' },
                        { value: 'M/d/yyyy', label: 'M/d/yyyy' },
                      ]}
                      placeholder={translate(
                        'referrals.ingestionMapper.modal.format',
                      )}
                      onChange={(format?: Selectable) => {
                        if (currentRules[idx].operation && format) {
                          currentRules[
                            idx
                          ].operation.param1 = format?.label as string;
                          setCurrentRules([...currentRules]);
                        }
                      }}
                      value={
                        item.operation?.param1
                          ? {
                              label: item.operation?.param1,
                              value: item.operation?.param1,
                            }
                          : undefined
                      }
                      size="small"
                    />
                  </>
                )}

                <Arrow className={classNames(idx === 0 && styles.firtsItem)} />
              </>
            ) : (
              <Arrow className={classNames(idx === 0 && styles.firtsItem)}>
                {!item.readonly &&
                  !!sourceFileHeaders?.length &&
                  !!referral?.length && (
                    <a
                      className={classNames(
                        'flex middle',
                        styles.addTransformationLink,
                      )}
                      onClick={(evt) => {
                        evt.preventDefault();
                        handleAddTransformation();
                      }}
                    >
                      <AddIcon fontSize="small" />
                      {translate(
                        'referrals.ingestionMapper.modal.addTransformation',
                      )}
                    </a>
                  )}
              </Arrow>
            )}
          </>
        )}

        <Select
          className={styles.select}
          disabled={!!item.readonly || (item.required && !isCandidate)}
          error={submitted && !item.targetColumn}
          items={getDestinationItems()}
          grouped={
            isCandidate
              ? (item: { value: string }) => {
                  const match = item.value.match(/^Candidate (\d+)/);
                  return match ? `Candidate ${match[1]}` : '';
                }
              : undefined
          }
          label={
            idx === 0
              ? translate('referrals.ingestionMapper.modal.destination')
              : undefined
          }
          placeholder={translate('referrals.ingestionMapper.modal.destination')}
          loading={loading.data}
          onChange={handleDestinationChange}
          value={
            isCandidate
              ? generateCandidateDestinationColumns(candidateTotal)?.find(
                  ({ value }) =>
                    value.replace(/^Candidate \d+ /, 'Candidate ') ===
                      item.targetColumn &&
                    item.candidateIndex ===
                      Number(value.match(/Candidate (\d+)/)?.[1]),
                ) ||
                (item.targetColumn
                  ? {
                      value: item.targetColumn,
                      label: item.targetColumn,
                      info: {
                        name: item.targetColumn,
                        required: false,
                        candidateIndex: 0,
                      },
                    }
                  : undefined)
              : destinationColumns?.find(
                  ({ value }) => value === item.targetColumn,
                )
          }
          size="small"
        />

        <IconButton
          className={classNames(idx === 0 && styles.firtsItem)}
          disabled={
            !!item.readonly ||
            (item.required && !isCandidate) ||
            currentRules.length < 2
          }
          size="small"
          onClick={handleDeleteRule}
        >
          <DeleteOutlineIcon fontSize="large" />
        </IconButton>
      </div>

      {item.operation?.action ===
        ReferralMappingOperationType.SPLIT_MULTIPLE && (
        <div
          className={classNames(
            'grid-span-12',
            styles.rowMappingContainer,
            styles.rowMappingSplitContainer,
            styles.rowMappingContainerBorder,
          )}
        >
          {!!item.operation &&
            item.operation?.dataDictionary?.map((data, splitIdx) => (
              <div
                className={classNames('flex middle', styles.row)}
                key={`referral-mixed-ingestion-rule-${splitIdx}-split-multiple`}
              >
                <TextInput
                  className={styles.selectExtraMin}
                  error={submitted && !data.sourceValue?.length}
                  label={
                    splitIdx === 0 ? (
                      <span
                        className="flex middle"
                        style={{ height: '17.14px' }}
                      >
                        {capitalize(
                          translate('referrals.ingestionMapper.modal.split'),
                        )}
                        {' * '}
                        <a
                          className={classNames(
                            'flex middle',
                            styles.addItemLink,
                          )}
                          onClick={(evt) => {
                            evt.preventDefault();
                            if (item.operation) {
                              item.operation.dataDictionary = [
                                ...item.operation.dataDictionary,
                                new ReferralMappingMappedValue({}),
                              ];
                              setCurrentRules([...currentRules]);
                            }
                          }}
                        >
                          <AddIcon
                            className={styles.addItemIcon}
                            fontSize="small"
                          />
                          {translate(
                            'referrals.ingestionMapper.modal.addSplitRule',
                          )}
                        </a>
                      </span>
                    ) : undefined
                  }
                  placeholder={capitalize(
                    translate('referrals.ingestionMapper.modal.split'),
                  )}
                  onChange={(value?: string) => {
                    if (item.operation) {
                      item.operation.dataDictionary[splitIdx].sourceValue =
                        value || '';
                      setCurrentRules([...currentRules]);
                    }
                  }}
                  value={data.sourceValue}
                  size="small"
                />
                <Arrow
                  className={classNames(splitIdx === 0 && styles.firtsItem)}
                />
                <Select
                  className={styles.selectExtraMin}
                  disableClearable
                  error={submitted && !data.destinationValue}
                  items={SPLIT_OPTIONS}
                  label={
                    splitIdx === 0
                      ? capitalize(
                          translate('referrals.ingestionMapper.modal.get'),
                        )
                      : undefined
                  }
                  placeholder={capitalize(
                    translate('referrals.ingestionMapper.modal.get'),
                  )}
                  onChange={(destinationValue: Selectable) => {
                    if (item.operation) {
                      item.operation.dataDictionary[splitIdx].destinationValue =
                        destinationValue?.value?.toString() || '';
                      setCurrentRules([...currentRules]);
                    }
                  }}
                  value={SPLIT_OPTIONS?.find(
                    ({ value }) => value.toString() === data.destinationValue,
                  )}
                  size="small"
                />
                <IconButton
                  className={classNames(splitIdx === 0 && styles.firtsItem)}
                  disabled={
                    item.operation?.dataDictionary &&
                    item.operation.dataDictionary.length < 2 &&
                    !item.operation.dataDictionary[0]?.sourceValue &&
                    !item.operation.dataDictionary[0]?.destinationValue
                  }
                  size="small"
                  onClick={() => {
                    if (item.operation?.dataDictionary) {
                      if (item.operation.dataDictionary.length > 1) {
                        item.operation.dataDictionary?.splice(splitIdx, 1);
                      } else {
                        item.operation.dataDictionary = [
                          new ReferralMappingMappedValue({}),
                        ];
                      }
                      setCurrentRules([...currentRules]);
                    }
                  }}
                >
                  <DeleteOutlineIcon fontSize="large" />
                </IconButton>
              </div>
            ))}
          <div className={classNames('flex middle', styles.row)}>
            <Select
              className={styles.selectLarge}
              disableClearable
              error={submitted && !item.operation?.param1}
              items={NO_MATCH_OPTIONS}
              label={translate('referrals.ingestionMapper.modal.noMatchLabel')}
              placeholder={translate(
                'referrals.ingestionMapper.modal.selectOption',
              )}
              onChange={(noMatchOption: Selectable) => {
                if (item.operation) {
                  item.operation.param1 =
                    noMatchOption?.value?.toString() || '';
                  setCurrentRules([...currentRules]);
                }
              }}
              value={NO_MATCH_OPTIONS?.find(
                ({ value }) => value === item.operation?.param1,
              )}
              size="small"
            />
          </div>
        </div>
      )}

      {item.operation?.action === ReferralMappingOperationType.BRANCH &&
        !!mixedReferrals?.length &&
        !loading.fileBranches &&
        renderMappingOptions(
          item.operation,
          fileData.branches,
          translate('referrals.ingestionMapper.modal.referral'),
          mixedReferrals,
          (item: SelectableInfo<Organization>) =>
            item?.info?.parent?.name || '',
          'referral',
        )}

      {item.operation?.action === ReferralMappingOperationType.GENDER &&
        !loading.fileGenders &&
        renderMappingOptions(
          item.operation,
          fileData.genders,
          translate('referrals.ingestionMapper.modal.gender'),
          Enum.toSelectable(PatientGender.asArray),
          undefined,
          'gender',
        )}

      {!isCandidate &&
        item.operation?.action === ReferralMappingOperationType.LANGUAGE &&
        !loading.fileLanguages &&
        renderMappingOptions(
          item.operation,
          fileData.languages,
          translate('referrals.ingestionMapper.modal.language'),
          Enum.toSelectable(Language.asArray),
          undefined,
          'language',
        )}

      {isCandidate &&
        item.operation?.action === ReferralMappingOperationType.BOOLEAN &&
        !loading.booleanColumns[item.sourceColumn || ''] &&
        renderMappingOptions(
          item.operation,
          fileData.booleanColumns[item.sourceColumn || ''] || [],
          translate('referrals.ingestionMapper.modal.boolean'),
          Enum.toSelectable(Choice.asArray),
          undefined,
          'boolean',
          ReferralMappingRosterType.CANDIDATE.value,
        )}

      {isCandidate &&
        item.operation?.action === ReferralMappingOperationType.RELATIONSHIP &&
        !loading.relationshipColumns[item.sourceColumn || ''] &&
        renderMappingOptions(
          item.operation,
          fileData.relationshipColumns[item.sourceColumn || ''] || [],
          translate('referrals.ingestionMapper.modal.relationship'),
          relationships,
          (item: SelectableInfo<any>) => item?.info?.group || '',
          'relationship',
          ReferralMappingRosterType.CANDIDATE.value,
        )}
    </>
  );
};
